<template>
	<TheForm :model="request" :rules="rules" ref="formRef" :waiting="waiting" :id="id">
		<ButtonSticky @buttonclick="createComplaint()">
			<template v-slot:icon><saveIcon /></template>
			<template v-slot:text> Speichern </template>
		</ButtonSticky>

		<section>
			<ButtonBack />

			<el-row>
				<el-col :sm="24" class="itemtitle">
					<el-form-item prop="title">
						<InputLite
							:inputValue="request.title"
							:placeholder="'Titel der Beschwerde'"
							@inputenter="(val) => (request.title = val)"
							@inputblur="(val) => (request.title = val)"
						/>
					</el-form-item>
				</el-col>
			</el-row>

			<TheBox :title="'Kontakt Beschwerdeführer'" :hasDataGrid="true">
				<el-row>
					<el-col :sm="12" :xs="24">
						<el-form-item label="Anrede:" class="datagrid--input-short" prop="gender">
							<el-select v-model="request.gender" :placeholder="'Bitte wählen'">
								<el-option v-for="item in genderOptions" :key="item.value" :label="item.label" :value="item" />
							</el-select>
						</el-form-item>

						<el-form-item label="Vorname:" prop="firstName">
							<el-input v-model="request.firstName" />
						</el-form-item>

						<el-form-item label="Nachname:" prop="lastName">
							<el-input v-model="request.lastName" />
						</el-form-item>
					</el-col>

					<el-col :sm="12" :xs="24">
						<el-form-item label="E-Mail-Adresse:" prop="email">
							<el-input v-model="request.email" />
						</el-form-item>

						<el-form-item label="Firma:" prop="company">
							<el-input v-model="request.company" />
						</el-form-item>

						<el-form-item label="Telefon:" prop="phone">
							<el-input v-model="request.phone" />
						</el-form-item>
					</el-col>
				</el-row>
			</TheBox>

			<TheBox :title="'Beschwerde Infos'" :hasDataGrid="true">
				<el-row>
					<el-col :sm="12" :xs="24">
						<el-form-item label="Beschwerdegrund:" prop="category">
							<el-select v-model="selectedCategory" :placeholder="'Bitte wählen'">
								<el-option v-for="option in categoryOptions" :key="option.value.number" :label="option.label" :value="option.value.number" />
							</el-select>
						</el-form-item>

						<el-form-item label="Datum:" prop="createdDate">
							<el-date-picker v-model="request.createdDate" type="date" placeholder="TT.MM.JJJJ" :format="'DD.MM.YYYY'" />
						</el-form-item>
					</el-col>

					<el-col :sm="12" :xs="24">
						<el-form-item label="Medium:" prop="medium">
							<el-select v-model="selectedMedium" :placeholder="'Bitte wählen'">
								<el-option v-for="option in mediaOptions" :key="option.value.name" :label="option.label" :value="option.value.name" />
							</el-select>
							<!-- <v-select
								v-model="request.medium"
								label="label"
								:options="mediaOptions"
								:clearable="false"
								:searchable="false"
								:placeholder="'Bitte wählen'"
							/> -->
						</el-form-item>
					</el-col>
				</el-row>
			</TheBox>

			<TheBox :title="'Beschwerde'">
				<el-form-item prop="text">
					<el-input v-model="request.text" :autosize="{ minRows: 6 }" type="textarea" placeholder="Bitte Text der Beschwerde hier eingeben." />
				</el-form-item>
			</TheBox>
		</section>

		<DetailsMedia :files="request.files ? request.files : []" @addToFiles="addToFiles" @removeFromFiles="removeFromFiles" :pageType="'beschwerde'" />
		<DetailsLinks :links="request.links ? request.links : []" @addToLinks="addToLinks" @removeFromLinks="removeFromLinks" :pageType="'beschwerde'" />

		<section>
			<TheBox :title="'Interne Notiz (nur für Mitarbeiter sichtbar)'">
				<el-form-item prop="internalNote">
					<el-input v-model="request.internalNote" :autosize="{ minRows: 3 }" type="textarea" placeholder="Interne Notiz" />
				</el-form-item>
			</TheBox>
		</section>
	</TheForm>

	<DialogLeavePage v-model:open="openClosingDialog" @cancel="leavePage" @save="createComplaint(true)" />
</template>

<script lang="ts">
import ButtonBack from '../components/buttons/ButtonBack.vue';
import ButtonSticky from '../components/buttons/ButtonSticky.vue';
import DialogLeavePage from '../components/dialogs/DialogLeavePage.vue';
import TheBox from '../components/TheBox.vue';
import TheButton from '../components/TheButton.vue';
import TheForm from '../components/TheForm.vue';
import { Delete, Plus, Loading } from '@element-plus/icons-vue';
import InputLite from '../components/forms/InputLite.vue';
import DetailsLinks from './sections/DetailsLinks.vue';
import DetailsMedia from './sections/DetailsMedia.vue';
import { ElMessage, FormInstance } from 'element-plus';
import { defineComponent, ref, markRaw } from 'vue';
import saveIcon from '../assets/icons/saveIcon.vue';
import { returnFormOptions } from '../utils/formOptions.js';
import { texts } from '../utils/texts';
import { ComplaintRequest } from '@/types/formdataTypes';
import { FileDto, LinkDto } from '@/types/types';
import { validateForm, validateCategory, validateMedium, hasFormChanged, resetForm } from '@/utils/functions';
import type { RouteLocationRaw } from 'vue-router';
import { createComplaint } from '@/requests/complaints';
import { getStartOfDayWithTime } from '@/utils/functions';

const ElMessageDuration = 3000;

interface NewComplaintsData {
	request: ComplaintRequest;
	initialData: ComplaintRequest;
	id: string;
	openClosingDialog: boolean;
	waiting: boolean;
	rules: Object;
	checkPageLeave: boolean;
	pageLeaveTo: RouteLocationRaw | null;
}

const initialRequestObj = {
	title: '',
	category: {
		number: '',
	},
	email: '',
	firstName: '',
	lastName: '',
	gender: null,
	medium: {
		name: '',
	},
	phone: '',
	company: '',
	text: '',
	files: [],
	links: [],
	internalNote: '',
	createdDate: '',
} as ComplaintRequest;

export default defineComponent({
	name: 'BeschwerdeNeuView',
	data(): NewComplaintsData {
		return {
			request: initialRequestObj,
			initialData: JSON.parse(JSON.stringify(initialRequestObj)), // deep copy to ensure different objects
			id: 'complaintForm',
			openClosingDialog: false,
			waiting: false,
			rules: {
				title: [{ required: true, message: texts.formErrors.reqMissing, trigger: ['blur'] }],
				gender: [{ required: true, message: texts.formErrors.reqMissing, trigger: 'change' }],
				email: [
					{ required: true, message: texts.formErrors.reqMissing, trigger: 'blur' },
					{ type: 'email', message: texts.formErrors.emailReq, trigger: ['blur'] },
				],
				firstName: [{ required: true, message: texts.formErrors.reqMissing, trigger: 'blur' }],
				lastName: [{ required: true, message: texts.formErrors.reqMissing, trigger: 'blur' }],
				category: [{ required: true, validator: validateCategory, trigger: ['change', 'blur'] }],
				medium: [{ required: true, validator: validateMedium, trigger: 'change' }],
				text: [
					{ required: true, message: texts.formErrors.reqMissing, trigger: ['blur'] },
					{ min: 3, message: texts.formErrors.minChars, trigger: 'blur' },
				],
				createdDate: [{ required: true, message: texts.formErrors.reqMissing, trigger: ['blur'] }],
			},
			checkPageLeave: true,
			pageLeaveTo: null,
		};
	},
	components: {
		ButtonBack,
		ButtonSticky,
		TheButton,
		TheBox,
		Plus,
		Delete,
		DetailsMedia,
		DetailsLinks,
		saveIcon,
		InputLite,
		TheForm,
		DialogLeavePage,
		Loading,
	},
	setup() {
		const formRef = ref<FormInstance>();

		return {
			formRef,
		};
	},
	computed: {
		texts() {
			return texts;
		},
		genderOptions() {
			return returnFormOptions('gender');
		},
		categoryOptions() {
			return returnFormOptions('complaintAndCaseCategories');
		},
		mediaOptions() {
			return returnFormOptions('media');
		},
		selectedCategory: {
			get(): string {
				return this.request?.category?.number || '';
			},
			set(value: string) {
				if (this.request) {
					this.request.category = {
						number: value,
					};
				}
			},
		},
		selectedMedium: {
			get(): string {
				return this.request?.medium?.name || '';
			},
			set(value: string) {
				if (this.request) {
					this.request.medium = {
						name: value,
					};
				}
			},
		},
	},
	methods: {
		async createComplaint(leavePage = false) {
			this.openClosingDialog = false;

			const isValid = await validateForm(this.formRef, this.id);
			if (!isValid) return;

			this.waiting = true;
			const waitingMsg = ElMessage({ message: texts.formErrors.waiting, type: 'info', duration: 0, showClose: false, icon: markRaw(Loading) });

			const createRequest: ComplaintRequest = {} as ComplaintRequest;
			Object.entries(this.request).forEach(([key, val]) => {
				if (key === 'files' || key === 'links') return;
				if (typeof val === 'object' && val && 'value' in val) {
					createRequest[key] = val.value;
				} else {
					createRequest[key] = val;
				}
			});

			if (this.request.links && this.request.links.length > 0) {
				createRequest.links = this.request.links?.map((link) => {
					return {
						name: link.link.name,
						url: link.link.url,
						type: link.link.type,
					} as LinkDto;
				});
			}

			if (this.request.files && this.request.files.length > 0) {
				createRequest.files = this.request.files?.map((file) => {
					return {
						fileName: file.file.fileName,
						bytes: file.file.bytes,
						contentType: file.file.contentType,
					} as FileDto;
				});
			}

			if (this.request.createdDate) createRequest.createdDate = getStartOfDayWithTime(this.request.createdDate, 10);

			const result = await createComplaint(createRequest);
			if (result.success) {
				if (leavePage) this.leavePage();
				else {
					this.checkPageLeave = false;
					this.$router.push({ name: 'BeschwerdeDetail', params: { id: String(result.data.id) }, replace: true });
				}
			} else {
				ElMessage({ message: texts.complaints.saveFailed, type: 'error', duration: ElMessageDuration });
			}

			this.waiting = false;
			waitingMsg.close();
		},
		addToFiles(file) {
			if (!this.request.files) this.request.files = [];
			this.request.files.push(file);
		},
		removeFromFiles(file) {
			this.request.files = this.request.files?.filter((f) => f.file !== file.file);
		},
		removeFromLinks(link) {
			this.request.links = this.request.links?.filter((l) => l.link !== link.link);
		},
		addToLinks(link) {
			if (!this.request.links) this.request.links = [];
			this.request.links.push(link);
		},
		leavePage() {
			this.openClosingDialog = false;
			this.checkPageLeave = false;

			// @ts-ignore
			this.$router.push(this.pageLeaveTo);
		},
		handleBeforeRouteLeave(to, from, next) {
			if (this.checkPageLeave === false) {
				resetForm(this.formRef, this.id);
				next();
				return;
			}

			// check if request data has changed
			const hasChanged = hasFormChanged({ initialData: this.initialData, currentData: this.request });

			if (!hasChanged) {
				next();
				return;
			}

			// ABORT NAVIGATION & SHOW DIALOG
			this.openClosingDialog = true;
			this.pageLeaveTo = to;
			next(false);
		},
	},
	// watch: {
	// 	request: {
	// 		deep: true,
	// 		handler() {
	// 			console.log('request changed', this.request);
	// 		},
	// 	},
	// },
});
</script>
